import { List, Map, Set } from "immutable";
import { openingKeyFor, OpeningRecord, prettyOpeningsSize as prettyOpeningsSizeText } from "common/records/Opening";
import { OpeningEstimationRecord } from "common/records/OpeningEstimation";
import { EstimateLineItemRecord } from "common/records/EstimateLineItem";
import { RootState } from "common/reducers";
import { Nullable } from "common/types";
import { estimateGroup } from "common/selectors/estimateGroup.selectors";
import { estimateLineItemOpeningEstimationIds } from "common/selectors/estimateLineItem.selectors";
import { openingById } from "common/selectors/opening.selectors";
import { createSelector } from "reselect";

const rootKey = "openingEstimations";

/**
 * Loads byId key from openingEstimations
 *
 * @param {RootState} state The RootState
 * @returns {Map<number, OpeningEstimationRecord>} Map<number, OpeningEstimationRecord>
 */
export const byId = (state: RootState) => state.getIn([rootKey, "byId"]);

/**
 * The opening estimation for the given id, or undefined if not loaded.
 *
 * @param {RootState} state The RootState
 * @param {{ openingEstimationId: number }} props The opening estimation id
 * @returns {OpeningEstimationRecord | undefined} OpeningEstimationRecord | undefined
 */
export const openingEstimation = (
  state: RootState,
  props: { openingEstimationId: number },
): OpeningEstimationRecord | undefined => state.getIn([rootKey, "byId", props.openingEstimationId]);

/**
 * The next unsaved (negative) id used when minting client-side opening estimations.
 *
 * @param {RootState} state The RootState
 * @returns {number} the current unsaved id
 */
export const unsavedId = (state: RootState): number => state.getIn([rootKey, "unsavedId"]) as number;

/**
 * The line item's opening estimations (all ids, incl. soft-deleted), resolved from the normalized
 * slice. Use this for serialization-adjacent reads; use `openings`/`openingCount` for live display.
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The estimate line item id
 * @returns {List<OpeningEstimationRecord>} the line item's opening estimations
 */
export const openingEstimations = createSelector(
  [estimateLineItemOpeningEstimationIds, byId],
  (openingEstimationIds, byId) => {
    return openingEstimationIds.map((id) => byId.get(id)).filter((oe): oe is OpeningEstimationRecord => Boolean(oe));
  },
);

/**
 * A line item's LIVE (non-destroyed) opening estimations, resolved from the normalized slice by its
 * `opening_estimation_ids`. Replaces the retired embedded `opening_estimations` helper.
 *
 * @param {RootState} state The RootState
 * @param {EstimateLineItemRecord} lineItem The line item
 * @returns {List<OpeningEstimationRecord>} the live opening estimations
 */
const liveLineItemOpeningEstimations = (
  state: RootState,
  lineItem: EstimateLineItemRecord,
): List<OpeningEstimationRecord> => {
  const estimationsById = byId(state) as Map<number, OpeningEstimationRecord>;
  return lineItem.opening_estimation_ids
    .map((id) => estimationsById.get(id))
    .filter((oe): oe is OpeningEstimationRecord => Boolean(oe) && !oe._destroy);
};

/**
 * Loads the LIVE openings from the opening estimations. Persisted estimations resolve their openable
 * from the windows/doors stores; unsaved attachments fall back to the client-side `openable` set at
 * attach time. Unresolvable openings are dropped. (Wizard behavior: keeps the unsaved-`openable`
 * fallback that master's non-wizard `openings` lacks.)
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The estimate line item id
 * @returns {List<OpeningRecord>} the resolved live openings
 */
export const openings = createSelector(
  [openingEstimations, (state: RootState) => state],
  (openingEstimations, state) => {
    return openingEstimations
      .filterNot((oe) => Boolean(oe._destroy))
      .map((oe) => {
        const type = oe.openable_type;
        const id = oe.openable_id;
        if (!type || !id) return oe.openable;

        return openingById(state, { openingId: id, openingType: type }) ?? oe.openable;
      })
      .filter((opening): opening is OpeningRecord => Boolean(opening));
  },
);

/**
 * Resolves a line item's first live (non-destroyed) opening at the point of use — persisted
 * estimations resolve their openable from the windows/doors stores; unsaved attachments fall back to
 * the client-side `openable` captured at attach time. Returns null when the line item has no live
 * opening or none resolves.
 *
 * @param {RootState} state The RootState
 * @param {Nullable<EstimateLineItemRecord>} lineItem The line item
 * @returns {Nullable<OpeningRecord>} the resolved first opening
 */
export const firstLineItemOpening = (
  state: RootState,
  lineItem: Nullable<EstimateLineItemRecord>,
): Nullable<OpeningRecord> => {
  if (!lineItem) return null;
  const oe = liveLineItemOpeningEstimations(state, lineItem).first<OpeningEstimationRecord | undefined>();
  if (!oe) return null;
  if (oe.openable_type && oe.openable_id) {
    return openingById(state, { openingId: oe.openable_id, openingType: oe.openable_type }) ?? oe.openable;
  }
  return oe.openable;
};

/**
 * Pretty prints the openings sizes and groups them by location and size
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The estimate line item id
 * @returns {string} Pretty printed openings sizes
 */
export const prettyOpeningsSize = createSelector([openings], (openings) => prettyOpeningsSizeText(openings));

/**
 * Count of the line item's live (non-destroyed) opening estimations. Reflects unsaved edits, so a
 * freshly built line item reports its real count rather than 0.
 *
 * @param {RootState} state The RootState
 * @param {{ estimateLineItemId: number }} props The estimate line item id
 * @returns {number} the live opening estimation count
 */
export const openingCount = createSelector(
  [openingEstimations],
  (openingEstimations) => openingEstimations.filterNot((oe) => Boolean(oe._destroy)).size,
);

const EMPTY_USED_KEYS = Set<string>();

/**
 * Identity keys ("Kind:id") of openings already attached to OTHER live line items in a group — the
 * wizard's "Added" state. `excludeLineItemId` leaves out the line item being edited so its own
 * openings stay selectable.
 *
 * @param {RootState} state The RootState
 * @param {{ estimateGroupId: number; excludeLineItemId?: Nullable<number> }} props
 * @returns {Set<string>} the used opening keys
 */
export const usedOpeningKeysInGroup = createSelector(
  [
    (state: RootState, props: { estimateGroupId: number }) =>
      estimateGroup(state, { estimateGroupId: props.estimateGroupId }),
    (state: RootState) => state.getIn(["estimateLineItems", "byId"]) as Map<number, EstimateLineItemRecord>,
    byId,
    (_state: RootState, props: { excludeLineItemId?: Nullable<number> }) => props.excludeLineItemId ?? null,
  ],
  (group, lineItemsById, oeById: Map<number, OpeningEstimationRecord>, excludeLineItemId): Set<string> => {
    if (!group) return EMPTY_USED_KEYS;
    let keys = Set<string>();
    group.line_item_ids.forEach((lineItemId) => {
      if (lineItemId === excludeLineItemId) return;
      const lineItem = lineItemsById.get(lineItemId);
      if (!lineItem || lineItem._destroy) return;
      lineItem.opening_estimation_ids.forEach((id) => {
        const oe = oeById.get(id);
        if (oe && !oe._destroy && oe.openable_type) keys = keys.add(openingKeyFor(oe.openable_type, oe.openable_id));
      });
    });
    return keys;
  },
);
